import { gql } from "@apollo/client";
import { Button, ButtonGroup, IconButton, Stack, Text } from "@chakra-ui/react";
import React, { Fragment } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import { EditIcon, PlusIcon } from "../../components/icons";
import { PageTitle } from "../../components/Page";
import { PageSpinner } from "../../components/PageSpinner";
import { Pagination } from "../../components/Pagination";
import { Panel } from "../../components/Panel";
import { DateCell, Table } from "../../components/Table";
import { buildNewSiteVisitPath, RouteParams } from "../../config/routes";
import { GetSiteVisitsQuery, useGetSiteVisitsQuery } from "../../generated/graphql";
import { useSingleParam } from "../../hooks/useSingleParam";

export const GET_SITE_VISITS = gql`
  query GetSiteVisits($slug: String!, $offset: Int, $limit: Int) {
    siteVisits(
      where: { supplier: { slug: { equals: $slug } } }
      orderBy: { visitDate: desc }
      skip: $offset
      take: $limit
    ) {
      id
      visitor
      visitDate
      comments
      truncatedComments(input: { max: 25 })
    }

    siteVisitCount(slug: $slug)
  }
`;

export const SiteVisitsListPage = () => {
  const supplierSlug = useSingleParam(RouteParams.SupplierSlug, { required: true });

  const [paginationData, setPaginationData] = React.useState({
    currentPage: 1,
    pageSize: 25,
    totalPages: 0,
  });

  const { loading, error, data } = useGetSiteVisitsQuery({
    variables: {
      slug: supplierSlug,
      offset: (paginationData.currentPage - 1) * paginationData.pageSize,
      limit: paginationData.pageSize,
    },
  });

  if (loading) {
    return <PageSpinner />;
  }

  if (error) {
    console.error(error);
    return (
      <Stack spacing={4}>
        <Text>Something went wrong loading the visits</Text>
      </Stack>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>WMS | Visits List</title>
      </Helmet>

      <Stack spacing={6} px={8} py={5}>
        <Stack width="100%" isInline justifyContent="space-between" alignItems="center">
          <PageTitle>Visits</PageTitle>
          <ButtonGroup>
            <Button
              as={Link}
              to={buildNewSiteVisitPath(supplierSlug)}
              colorScheme="blue"
              leftIcon={<PlusIcon />}
            >
              Create new visit
            </Button>
          </ButtonGroup>
        </Stack>

        <Panel height="65vh" overflowY="auto">
          <Table
            data-testid="quotes-list"
            data={data?.siteVisits ?? []}
            columns={siteVisitsTableColumn}
          />
        </Panel>

        <Pagination
          currentPage={paginationData.currentPage}
          pageSize={paginationData.pageSize}
          totalCount={data?.siteVisitCount ?? 1}
          onGoToPage={(pageNumber) => {
            setPaginationData({
              ...paginationData,
              currentPage: pageNumber,
            });
          }}
        />
      </Stack>
    </Fragment>
  );
};

const SiteVisitCommentAccessor = (siteVisit: GetSiteVisitsQuery["siteVisits"][0]) => {
  return {
    truncated: siteVisit.truncatedComments,
    original: siteVisit.comments,
  };
};

const siteVisitsTableColumn = [
  { Header: "Dated", accessor: "visitDate", Cell: DateCell },
  { Header: "Visited By", accessor: "visitor" },
  {
    Header: "Comments",
    accessor: SiteVisitCommentAccessor,
    Cell: (props: { value: ReturnType<typeof SiteVisitCommentAccessor> }) => {
      return <span title={props.value.original}>{props.value.truncated}</span>;
    },
  },
  {
    id: "actions",
    Header: "",
    accessor: "id",
    Cell: (props: { value: string }) => {
      return (
        <Stack direction="row">
          <Link to={`${props.value}/edit`}>
            <IconButton colorScheme="green" aria-label="Edit site visit" icon={<EditIcon />} />
          </Link>
        </Stack>
      );
    },
  },
];
